import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoCourseYear } from "@/shared/model/edo-course-year.model";
import { IEdoSubject } from "@/shared/model/edo-subject.model";
import { IEdoVideo } from "@/shared/model/edo-video.model";
import {
  IEdoVideostorePost,
  defaultValue,
} from "@/shared/model/edo-videostore-post.model";
import { EdoEducationalStage } from "@/shared/model/enumerations/edo-educational-stage.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { getEntities as getEdoClasseList } from "@/shared/reducers/entities/edo-class.reducer";
import { getEntities as getEdoSubjectList } from "@/shared/reducers/entities/edo-subject.reducer";
import { updateEntity } from "@/shared/reducers/entities/edo-videostore-post.reducer";
import { getVideoStatusLabel } from "../videostore";
import axios from "axios";
import { SocketContext } from "context/socket";
import "moment/locale/it";
import React, { createRef, useContext, useEffect, useState } from "react";
import { Translate, translate } from "react-jhipster";
import { z } from "zod";
import { Controller, useFormContext } from "react-hook-form";
import { Loader2, Upload, Video, X } from "lucide-react";
import { cn } from "@/shared/util/cn";

type IEditVideostorePostProps = {
  videostorePost: IEdoVideostorePost | null;
  open: boolean;
  onClose: () => void;
  isNew?: boolean;
};

const EditVideostorePost = (props: IEditVideostorePostProps) => {
  const dispatch = useAppDispatch();
  const socket = useContext(SocketContext);
  const account = useAppSelector<AuthenticationState>((s) => s.authentication.account);
  const { loading, updating, updateSuccess } = useAppSelector<
    EntityState<IEdoVideostorePost>
  >((state) => state.edoVideostorePost);
  const { entities: edoSubjectList, loading: loadingSubject } = useAppSelector<
    EntityState<IEdoSubject>
  >((state) => state.edoSubject);
  const { entities: edoCourseYearList } = useAppSelector<EntityState<IEdoCourseYear>>(
    (state) => state.edoCourseYear
  );
  const [form, setForm] = useState<IEdoVideostorePost>({
    ...defaultValue,
    ...props.videostorePost,
  });
  const [uploading, setUploading] = useState<boolean>(false);
  const fileInput = createRef<HTMLInputElement>();
  const { videostorePost, open, onClose: handleClose, isNew } = props;

  useEffect(() => {
    dispatch(getEdoClasseList({ page: 0, size: 1000, sort: "id:ASC" }));
    dispatch(getEdoSubjectList({ page: 0, size: 1000, sort: "name:ASC" }));
  }, []);

  useEffect(() => {
    if (!open) {
      setForm({ ...defaultValue });
      return;
    }
    if (videostorePost) {
      setForm({ ...defaultValue, ...videostorePost });
    } else {
      setForm({ ...defaultValue });
    }
  }, [videostorePost, open, isNew]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = async (data?: {
    title?: string;
    years?: { id: string }[];
    subject?: IEdoSubject;
    educationalStage?: EdoEducationalStage;
  }) => {
    const payload = data ? { ...form, ...data } : form;
    dispatch(updateEntity({ ...payload }));
  };

  const schema = z.object({
    title: z
      .string()
      .min(1, translate("edocendoApp.edoVideostorePost.title") + " required"),
    yearIds: z
      .array(z.union([z.string(), z.number()]))
      .min(1, "Anni required"),
    subjectId: z.number({
      required_error:
        translate("edocendoApp.edoVideostorePost.subject") + " required",
    }),
    educationalStage: z.nativeEnum(EdoEducationalStage),
  });

  const selectFiles = () => {
    fileInput.current?.click();
  };

  const handleFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setUploading(true);
    const files = [...Array.from(event.target.files || [])];
    if (files.length) {
      const file = files[0];
      const formData = new FormData();
      formData.append("file", file, file.name);
      const response = await axios.post<{ data: IEdoVideostorePost }>(
        "/videostore-posts",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setUploading(false);
      setForm({ ...response.data.data });
    }
  };

  const handleOnVideoUpdate = (video: IEdoVideo) => {
    if (video.id === form?.video?.id) {
      setForm((prevState) => ({
        ...prevState,
        video: { ...prevState.video, status: video.status },
      }));
    }
  };

  useEffect(() => {
    if (account?.id) {
      socket.on(`user:${account.id}:video-update`, handleOnVideoUpdate);
    }
    return () => {
      if (account?.id) {
        socket.off(`user:${account.id}:video-update`, handleOnVideoUpdate);
      }
    };
  }, [account, form]);

  const uploadDialog = (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        showClose={false}
        className="flex max-h-[80vh] max-w-md flex-col gap-0 overflow-hidden p-0"
      >
        <DialogHeader className="flex shrink-0 flex-row items-center justify-between gap-4 border-b border-[var(--color-mockup-border)] px-6 py-4 dark:border-[var(--color-mockup-border)]">
          <DialogTitle>Carica un video</DialogTitle>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-mockup-text-light)] transition-colors hover:bg-primary/10 hover:text-primary"
            aria-label="Chiudi"
          >
            <X className="h-4 w-4" />
          </button>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-5">
          <div className="flex flex-col items-center justify-center gap-4 py-4 text-center">
            <Avatar className="h-24 w-24 rounded-3xl bg-primary/10">
              <Upload className="h-12 w-12 text-primary" />
            </Avatar>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-[var(--color-mockup-text)]">
                Carica un file video
              </p>
              <p className="text-sm text-[var(--color-mockup-text-light)]">
                I tuoi video resteranno privati finché non li pubblichi.
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={selectFiles}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Aggiungi file video"
              )}
            </Button>
            <input
              type="file"
              ref={fileInput}
              name="files"
              multiple={false}
              hidden
              onChange={handleFiles}
              accept="video/mp4,video/quicktime"
            />
          </div>
          <p className="mt-4 text-center text-xs text-[var(--color-mockup-text-light)]">
            La dimensione massima del file è 2 GB. Formati supportati: MP4, MOV.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (isNew && !form?.id && !videostorePost) {
    return uploadDialog;
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b border-[var(--color-mockup-border)] px-6 py-4 dark:border-[var(--color-mockup-border)]">
          <DialogTitle>
            {translate("edocendoApp.edoVideostorePost.home.editLabel")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-5">
          <p className="mb-4 text-sm text-[var(--color-mockup-text-light)]">
            Inserisci le informazioni del contenuto videoteca. Puoi selezionare
            uno o più anni e uno o più istituti.
          </p>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Form
              id="edit-videostore-post-form"
              schema={schema}
              onSubmit={(data) =>
                saveEntity({
                  title: data.title,
                  years: data.yearIds.map((id) => ({ id: String(id) })),
                  subject: data.subjectId
                    ? ({ id: data.subjectId } as IEdoSubject)
                    : undefined,
                  educationalStage: data.educationalStage,
                })
              }
              defaultValues={{
                title: form.title ?? "",
                yearIds:
                  form.years?.map((el) =>
                    typeof el.id === "number"
                      ? el.id
                      : parseInt(String(el.id), 10)
                  ) ?? [],
                subjectId: form.subject?.id ?? 0,
                educationalStage:
                  (form.educationalStage as EdoEducationalStage) ??
                  EdoEducationalStage.PRIMARIA,
              }}
              className="flex flex-col gap-4"
            >
            <FormField
              name="title"
              label={translate("edocendoApp.edoVideostorePost.title")}
            />
            <Controller
              name="yearIds"
              render={({ field }) => (
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-[var(--color-mockup-text)]">
                    Anni <span className="text-[var(--color-mockup-coral)]">*</span>
                  </label>
                  <select
                    multiple
                    value={field.value.map(String)}
                    onChange={(e) => {
                      const selected = Array.from(
                        e.target.selectedOptions,
                        (o) => o.value
                      ).map((v) => (v.match(/^\d+$/) ? parseInt(v, 10) : v));
                      field.onChange(selected);
                    }}
                    className={cn(
                      "flex min-h-[80px] w-full rounded-2xl border-2 border-[var(--color-mockup-border)] bg-surface px-3 py-2 text-sm focus:outline-none focus:border-primary focus:shadow-[0_4px_12px_rgba(18,181,170,0.15)] transition-all duration-200"
                    )}
                  >
                    {edoCourseYearList?.map((annoCorso) => (
                      <option key={annoCorso.id} value={annoCorso.id}>
                        {annoCorso.year}
                      </option>
                    )) ?? null}
                  </select>
                </div>
              )}
            />
            <Controller
              name="subjectId"
              render={({ field }) => (
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-[var(--color-mockup-text)]">
                    {translate("edocendoApp.edoVideostorePost.subject")}{" "}
                    <span className="text-[var(--color-mockup-coral)]">*</span>
                  </label>
                  <Select
                    value={String(field.value)}
                    onValueChange={(v) => field.onChange(parseInt(v, 10))}
                    disabled={loadingSubject}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={translate("edocendoApp.edoVideostorePost.subject")} />
                    </SelectTrigger>
                    <SelectContent>
                      {edoSubjectList?.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.name}
                        </SelectItem>
                      )) ?? null}
                    </SelectContent>
                  </Select>
                  {loadingSubject && (
                    <Loader2 className="mt-1 h-4 w-4 animate-spin text-[var(--color-mockup-text-light)]" />
                  )}
                </div>
              )}
            />
            <Controller
              name="educationalStage"
              render={({ field }) => (
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-[var(--color-mockup-text)]">
                    {translate("edocendoApp.edoVideostorePost.educationalStage")}{" "}
                    <span className="text-[var(--color-mockup-coral)]">*</span>
                  </label>
                  <Select
                    value={field.value ?? ""}
                    onValueChange={(v) =>
                      field.onChange(v as EdoEducationalStage)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        EdoEducationalStage.INFANZIA,
                        EdoEducationalStage.PRIMARIA,
                        EdoEducationalStage.SECONDARIA_I,
                        EdoEducationalStage.SECONDARIA_II,
                      ].map((stage) => (
                        <SelectItem key={stage} value={stage}>
                          <Translate
                            contentKey={`edocendoApp.EdoEducationalStage.${stage}`}
                          />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              />
            </Form>
          )}
          {form.video && (
            <div className="mt-4 flex h-16 items-center gap-3 overflow-hidden rounded-2xl border-2 border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50">
              <div className="flex h-full w-14 shrink-0 items-center justify-center bg-primary">
                <Video className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0 flex-1 py-2">
                <p className="truncate text-sm font-semibold text-[var(--color-mockup-text)]">
                  {form.video.originalFileName}
                </p>
                <div className="text-xs text-[var(--color-mockup-text-light)]">
                  {getVideoStatusLabel(form.video.status)}
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="shrink-0 gap-2 border-t border-[var(--color-mockup-border)] px-6 py-4 dark:border-[var(--color-mockup-border)]">
          <Button type="button" variant="outline" onClick={handleClose}>
            <Translate contentKey="entity.action.cancel" />
          </Button>
          <Button
            type="submit"
            form="edit-videostore-post-form"
            disabled={updating}
          >
            {updating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              translate("entity.action.save")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditVideostorePost;
